<template>
  <div class="p-6">
    <!-- Encabezado Premium -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
      <div class="flex items-center gap-4">
        <div class="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-200 dark:shadow-none transition-transform hover:rotate-3">
          <Icon icon="mdi:cash-check" class="text-white text-3xl" />
        </div>
        <div>
          <h1 class="text-3xl font-black text-gray-900 dark:text-white tracking-tight">Criterios de Pago</h1>
          <p class="text-sm font-bold text-gray-400 dark:text-gray-500 flex items-center gap-1.5 uppercase tracking-widest mt-0.5">
            <span class="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
            Gestión de Estados Financieros
          </p>
        </div>
      </div>

      <div class="flex items-center gap-3 w-full md:w-auto">
        <!-- Exportación Inteligente -->
        <div class="relative group flex-1 md:flex-none">
          <button
            @click="showExportMenu = !showExportMenu"
            class="w-full flex items-center justify-center px-5 py-3.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm border border-gray-100 dark:border-gray-700 active:scale-95"
          >
            <Icon icon="mdi:printer-outline" class="mr-2.5 text-lg opacity-70" />
            Reportes
            <Icon icon="mdi:chevron-down" :class="['ml-2.5 text-lg transition-transform duration-300', showExportMenu ? 'rotate-180' : '']" />
          </button>
          
          <transition enter-active-class="transition duration-200 ease-out" enter-from-class="opacity-0 translate-y-2" enter-to-class="opacity-100 translate-y-0" leave-active-class="transition duration-150 ease-in" leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 translate-y-2">
            <div v-if="showExportMenu" class="absolute right-0 mt-3 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 z-[60] overflow-hidden">
              <div class="p-2 space-y-1">
                <button @click="printFullReport" class="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl flex items-center group transition-colors">
                  <div class="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:file-pdf-box" class="text-xl text-red-500" />
                  </div>
                  <div>
                    <p class="text-sm font-black text-gray-800 dark:text-gray-200">Completo</p>
                    <p class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Estadísticas + Tabla</p>
                  </div>
                </button>
                <button @click="printSimplifiedReport" class="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl flex items-center group transition-colors">
                  <div class="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg mr-3 group-hover:scale-110 transition-transform">
                    <Icon icon="mdi:table-large" class="text-xl text-blue-500" />
                  </div>
                  <div>
                    <p class="text-sm font-black text-gray-800 dark:text-gray-200">Simplificado</p>
                    <p class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Solo listado esencial</p>
                  </div>
                </button>
              </div>
            </div>
          </transition>
        </div>
        
        <button
          @click="openCreateModal"
          class="flex-1 md:flex-none flex items-center justify-center px-6 py-3.5 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 dark:shadow-none hover:translate-y-[-2px] active:scale-95"
        >
          <Icon icon="mdi:plus-thick" class="mr-2 text-lg" />
          Nuevo Estado
        </button>
      </div>
    </div>

    <!-- Filtros Inteligentes Premium -->
    <div class="bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-100/50 dark:shadow-none p-6 mb-8 border border-gray-100 dark:border-gray-700 transition-all">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
        <!-- Búsqueda Principal -->
        <div class="md:col-span-12 lg:col-span-5">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Búsqueda Global</label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-transform group-focus-within:scale-110">
              <Icon icon="mdi:magnify" class="text-xl text-blue-500 opacity-50" />
            </div>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Ej: Liquidado, Pendiente, Error..."
              class="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-900/50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 shadow-sm text-sm font-bold transition-all placeholder:text-gray-300 dark:placeholder:text-gray-600"
              @input="handleSearch"
            />
          </div>
        </div>

        <!-- Segmentación por Color -->
        <div class="md:col-span-6 lg:col-span-3">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Semántica Visual</label>
          <div class="relative">
            <select
              v-model="filterColor"
              @change="handleFilterChange"
              class="w-full appearance-none border-none bg-white dark:bg-gray-900/50 rounded-2xl px-5 py-4 text-sm font-black focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer pr-10"
            >
              <option value="all">TODOS LOS NIVELES</option>
              <option value="success">ÉXITO (VERDE)</option>
              <option value="warning">ALERTA (AMARILLO)</option>
              <option value="danger">CRÍTICO (ROJO)</option>
              <option value="info">INFORMATIVO</option>
            </select>
            <Icon icon="mdi:palette-swatch" class="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none opacity-50" />
          </div>
        </div>

        <!-- Organización -->
        <div class="md:col-span-6 lg:col-span-2">
          <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Jerarquía</label>
          <div class="relative">
            <select
              v-model="sortBy"
              class="w-full appearance-none border-none bg-white dark:bg-gray-900/50 rounded-2xl px-5 py-4 text-sm font-black focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer pr-10"
            >
              <option value="nombre">A-Z ALPHA</option>
              <option value="fechaCreacion_desc">MÁS RECIENTES</option>
              <option value="color">POR SEMÁNTICA</option>
            </select>
            <Icon icon="mdi:sort-variant" class="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-gray-400 pointer-events-none opacity-50" />
          </div>
        </div>

        <!-- Acciones de Datos -->
        <div class="md:col-span-12 lg:col-span-2 flex items-end">
          <button
            @click="loadEstadosPago"
            class="w-full flex items-center justify-center p-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-lg"
          >
            <Icon icon="mdi:layers-refresh" :class="['mr-2 text-xl', loading ? 'animate-spin' : '']" />
            Sincronizar
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas Premium -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
            <Icon icon="mdi:tag-multiple" class="text-blue-600 dark:text-blue-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Estados</p>
            <h3 class="text-2xl font-black text-gray-900 dark:text-white">{{ estados.length }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
            <Icon icon="mdi:form-select" class="text-purple-600 dark:text-purple-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Definidos</p>
            <h3 class="text-2xl font-black text-gray-900 dark:text-white">{{ estadisticas.conDescripcion }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
            <Icon icon="mdi:palette-swatch" class="text-emerald-600 dark:text-emerald-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Diversidad</p>
            <h3 class="text-2xl font-black text-emerald-600 dark:text-emerald-400">{{ Object.keys(estadisticas.colores).length }}</h3>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-xl shadow-gray-100/50 dark:shadow-none transition-all hover:scale-[1.02]">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <Icon icon="mdi:clock-check-outline" class="text-indigo-600 dark:text-indigo-400 text-2xl" />
          </div>
          <div>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Recientes</p>
            <h3 class="text-2xl font-black text-indigo-600 dark:text-indigo-400">{{ estadisticas.recientes }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de Filtrado Premium -->
    <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 -translate-y-4" enter-to-class="opacity-100 translate-y-0" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-4">
      <div v-if="isFiltered" class="mb-8 flex flex-wrap items-center gap-3 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-2xl border border-blue-100 dark:border-blue-800/50">
        <div class="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-md">
          <Icon icon="mdi:filter-variant" class="text-sm" />
          Filtros Activos
        </div>
        
        <div v-if="searchQuery" class="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-900 rounded-xl shadow-sm">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Búsqueda:</span>
          <span class="text-xs font-bold text-blue-600 dark:text-blue-400">"{{ searchQuery }}"</span>
          <button @click="searchQuery = ''; handleSearch()" class="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
            <Icon icon="mdi:close" class="text-gray-400" />
          </button>
        </div>

        <div v-if="filterColor !== 'all'" class="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-900 rounded-xl shadow-sm">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-tighter">Nivel:</span>
          <span class="text-xs font-bold text-blue-600 dark:text-blue-400">{{ getColorName(filterColor) }}</span>
          <button @click="filterColor = 'all'; handleFilterChange()" class="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
            <Icon icon="mdi:close" class="text-gray-400" />
          </button>
        </div>

        <button @click="clearFilters" class="ml-auto text-[10px] font-black text-blue-500 hover:text-blue-700 uppercase tracking-[0.2em] transition-colors flex items-center gap-1.5">
          <Icon icon="mdi:filter-off" class="text-sm" />
          Reiniciar Vista
        </button>
      </div>
    </transition>

    <!-- Panel de Resultados Principal -->
    <div class="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl shadow-gray-200/50 dark:shadow-none overflow-hidden border border-gray-100 dark:border-gray-700 transition-all">
      <div class="px-8 py-6 border-b border-gray-50 dark:border-gray-700/50 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div>
          <h2 class="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
            Matriz de Estados
            <span class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-[10px] font-black rounded-lg uppercase tracking-widest">{{ filteredEstados.length }} ENTIDADES</span>
          </h2>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Sincronizado: {{ formatTime(lastUpdate) }}</p>
        </div>
      </div>

      <div class="overflow-x-auto custom-scrollbar">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50/50 dark:bg-gray-900/30">
              <th class="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100 dark:border-gray-700">Estado / Contexto</th>
              <th class="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100 dark:border-gray-700">Observaciones</th>
              <th class="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100 dark:border-gray-700">Categoría</th>
              <th class="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100 dark:border-gray-700">Registro</th>
              <th class="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100 dark:border-gray-700">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50 dark:divide-gray-700/50">
            <!-- Estado de carga -->
            <tr v-if="loading">
              <td colspan="5" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando estados de pago...</p>
                </div>
              </td>
            </tr>

            <!-- Sin resultados -->
            <tr v-else-if="filteredEstados.length === 0">
              <td colspan="5" class="px-6 py-24 text-center">
                <div class="flex flex-col items-center max-w-sm mx-auto p-12 bg-gray-50/50 dark:bg-gray-900/30 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-inner">
                  <div class="p-6 bg-white dark:bg-gray-800 rounded-3xl shadow-xl mb-6 transform hover:rotate-6 transition-transform">
                    <Icon icon="mdi:database-off-outline" class="text-5xl text-blue-500" />
                  </div>
                  <h3 class="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter mb-2">No se encontraron registros</h3>
                  <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-relaxed">
                    {{ searchQuery || filterColor !== 'all' ? 'La búsqueda no coincide con ninguna entidad en la matriz.' : 'No existen estados de pago definidos en el sistema.' }}
                  </p>
                  <button v-if="searchQuery || filterColor !== 'all'" @click="clearFilters" class="mt-8 px-8 py-3 bg-blue-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-blue-200 dark:shadow-none hover:bg-blue-700 transition-all active:scale-95">
                    Reiniciar Vista
                  </button>
                </div>
              </td>
            </tr>

            <!-- Registros -->
            <tr 
              v-else 
              v-for="estado in paginatedEstados" 
              :key="estado.idEstadoPago"
              class="group hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-all duration-300 border-b border-gray-100 dark:border-gray-700/50 last:border-none cursor-pointer"
              @click="openDetailModal(estado)"
            >
              <td class="px-6 py-5">
                <div class="flex items-center gap-4">
                  <div class="relative">
                    <div :class="[
                      'w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-sm border border-transparent',
                      `bg-${getColorClass(estado.color)}-50 dark:bg-${getColorClass(estado.color)}-900/20`
                    ]">
                      <Icon 
                        icon="mdi:credit-card-outline" 
                        :class="[
                          'text-2xl',
                          `text-${getColorClass(estado.color)}-600 dark:text-${getColorClass(estado.color)}-400`
                        ]" 
                      />
                    </div>
                    <div :class="[
                      'absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-gray-800 shadow-sm animate-pulse',
                      `bg-${getColorClass(estado.color)}-500`
                    ]"></div>
                  </div>
                  <div>
                    <div class="text-sm font-black text-gray-900 dark:text-white tracking-tight group-hover:text-blue-600 transition-colors capitalize">
                      {{ estado.nombre }}
                    </div>
                    <div class="text-[10px] font-bold text-gray-400 dark:text-gray-500 flex items-center mt-0.5 uppercase tracking-tighter">
                      <Icon icon="mdi:identifier" class="mr-1 opacity-50" />
                      PK: {{ estado.idEstadoPago }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-5">
                 <p class="text-[13px] font-medium text-gray-500 dark:text-gray-400 line-clamp-1 max-w-[250px]">
                  {{ estado.descripcion || 'Sin descripción detallada' }}
                </p>
              </td>
              <td class="px-6 py-5">
                <div :class="[
                  'inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest',
                  `bg-${getColorClass(estado.color)}-50 dark:bg-${getColorClass(estado.color)}-900/30 text-${getColorClass(estado.color)}-600 dark:text-${getColorClass(estado.color)}-400 border border-${getColorClass(estado.color)}-100 dark:border-${getColorClass(estado.color)}-800`
                ]">
                  <span :class="['w-1.5 h-1.5 rounded-full mr-2 shadow-sm animate-pulse', `bg-${getColorClass(estado.color)}-500`]" class="font-sans"></span>
                  {{ getColorName(estado.color) }}
                </div>
              </td>
              <td class="px-6 py-5 whitespace-nowrap">
                <div class="flex flex-col">
                  <span class="text-[13px] font-black text-gray-700 dark:text-gray-300">
                    {{ formatDate(estado.fecha_creacion || estado.fechaCreacion) }}
                  </span>
                  <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">
                    {{ formatTime(estado.fecha_creacion || estado.fechaCreacion) }}
                  </span>
                </div>
              </td>
              <td class="px-6 py-5 whitespace-nowrap text-right">
                <div class="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                  <button
                    @click.stop="openEditModal(estado)"
                    class="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm active:scale-95"
                    title="Editar Estado"
                  >
                    <Icon icon="mdi:pencil-outline" class="text-xl" />
                  </button>
                  <button
                    @click.stop="confirmDelete(estado)"
                    class="p-2.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-sm active:scale-95"
                    title="Eliminar Estado"
                  >
                    <Icon icon="mdi:delete-outline" class="text-xl" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación Premium -->
      <div v-if="filteredEstados.length > 0 && !loading" class="px-8 py-6 bg-gray-50/50 dark:bg-gray-900/30 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <div class="flex -space-x-2">
            <div v-for="i in 3" :key="i" class="w-7 h-7 rounded-full border-2 border-white dark:border-gray-800 bg-gray-200 dark:bg-gray-700"></div>
          </div>
          <p class="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
            Exhibiendo <span class="text-gray-900 dark:text-white">{{ startItem }}-{{ endItem }}</span> de <span class="text-gray-900 dark:text-white">{{ filteredEstados.length }}</span> registros
          </p>
        </div>

        <div class="flex items-center gap-2">
          <button
            @click="currentPage = Math.max(currentPage - 1, 1)"
            :disabled="currentPage === 1"
            class="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-blue-600 hover:border-blue-200 transition-all disabled:opacity-30 disabled:hover:text-gray-400"
          >
            <Icon icon="mdi:chevron-left" class="text-xl" />
          </button>
          
          <div class="flex items-center gap-1.5">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="currentPage = page"
              :class="[
                'w-10 h-10 rounded-xl text-xs font-black transition-all',
                currentPage === page
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 dark:shadow-none scale-110 z-10'
                  : 'text-gray-500 hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-gray-100 dark:hover:border-gray-700'
              ]"
            >
              {{ page }}
            </button>
          </div>
          
          <button
            @click="currentPage = Math.min(currentPage + 1, totalPages)"
            :disabled="currentPage === totalPages"
            class="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-blue-600 hover:border-blue-200 transition-all disabled:opacity-30 disabled:hover:text-gray-400"
          >
            <Icon icon="mdi:chevron-right" class="text-xl" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Maestro Creación/Edición -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden transform transition-all flex flex-col max-h-[90vh]">
            <div class="px-8 py-6 border-b border-gray-50 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 flex justify-between items-center">
              <div class="flex items-center gap-4">
                <div :class="`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transition-transform hover:rotate-6 ${editingEstado ? 'bg-blue-600 shadow-blue-200' : 'bg-emerald-600 shadow-emerald-200'}`">
                  <Icon :icon="editingEstado ? 'mdi:pencil-box' : 'mdi:plus-circle'" class="text-white text-2xl" />
                </div>
                <div>
                  <h3 class="text-xl font-black text-gray-900 dark:text-white leading-tight">{{ editingEstado ? 'Editar Estado' : 'Nuevo Estado' }}</h3>
                  <p class="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-0.5">Definición de Lógica de Pago</p>
                </div>
              </div>
              <button @click="closeModal" class="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-2xl transition-all active:scale-90">
                <Icon icon="mdi:close" class="text-xl text-gray-400" />
              </button>
            </div>
            
            <form @submit.prevent="saveEstado" class="p-8 overflow-y-auto custom-scrollbar space-y-7">
              <div class="space-y-2">
                <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Identificador Público</label>
                <div class="relative group">
                  <input
                    v-model="form.nombre"
                    type="text"
                    required
                    placeholder="Ej: Aprobado, Retenido..."
                    class="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900/50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 shadow-sm text-sm font-bold transition-all placeholder:text-gray-300"
                  />
                  <Icon icon="mdi:form-textbox" class="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                </div>
              </div>

              <div class="space-y-2">
                <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Regla de Negocio (Opcional)</label>
                <textarea
                  v-model="form.descripcion"
                  rows="3"
                  placeholder="Explique cuándo se aplica este estado..."
                  class="w-full px-5 py-4 bg-gray-50 dark:bg-gray-900/50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 shadow-sm text-sm font-bold transition-all resize-none placeholder:text-gray-300"
                ></textarea>
              </div>

              <div class="space-y-3">
                <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 text-center block">Semántica Visual</label>
                <div class="grid grid-cols-4 gap-4">
                  <button
                    v-for="colorOption in colorOptions.slice(0, 4)"
                    :key="colorOption.value"
                    type="button"
                    @click="form.color = colorOption.value"
                    :class="[
                      'group relative p-4 rounded-3xl border-2 transition-all flex flex-col items-center gap-2 hover:scale-105',
                      form.color === colorOption.value 
                        ? `border-${colorOption.colorClass}-500 bg-${colorOption.colorClass}-50 dark:bg-${colorOption.colorClass}-900/20` 
                        : 'border-gray-50 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 hover:border-gray-200'
                    ]"
                  >
                    <div :class="`w-8 h-8 rounded-2xl shadow-sm shadow-lg transition-transform group-hover:rotate-12 bg-${colorOption.colorClass}-500 shadow-${colorOption.colorClass}-200`"></div>
                    <span class="text-[9px] font-black uppercase tracking-tighter text-gray-500">{{ colorOption.label }}</span>
                    <Icon v-if="form.color === colorOption.value" icon="mdi:check-circle" :class="['absolute -top-1 -right-1 text-xl px-0.5 rounded-full', `text-${colorOption.colorClass}-500 bg-white dark:bg-gray-800`]" />
                  </button>
                </div>
              </div>

              <div v-if="form.nombre" class="mt-4 p-5 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2rem] shadow-xl overflow-hidden relative group">
                <div class="absolute -right-4 -top-4 opacity-10 flex rotate-12 group-hover:scale-110 transition-transform">
                  <Icon icon="mdi:credit-card-check" class="text-8xl text-white" />
                </div>
                <p class="text-[10px] font-black text-white/50 uppercase tracking-[0.2em] mb-3">Previsualización en tiempo real</p>
                <div class="flex items-center gap-4 bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/20">
                  <div :class="`w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg text-${getColorClass(form.color)}-600`">
                    <Icon icon="mdi:tag-check" class="text-2xl" />
                  </div>
                  <div>
                    <h5 class="text-white font-black text-sm uppercase">{{ form.nombre }}</h5>
                    <p class="text-white/60 text-[9px] font-bold tracking-widest line-clamp-1 italic">{{ form.descripcion || 'Esperando descripción...' }}</p>
                  </div>
                </div>
              </div>

              <div class="flex gap-4 pt-4">
                <button
                  type="button"
                  @click="closeModal"
                  class="flex-1 px-6 py-4 border border-gray-100 dark:border-gray-700 rounded-2xl text-gray-400 font-bold text-xs uppercase tracking-widest hover:bg-gray-50 transition-all active:scale-95"
                >
                  Descartar
                </button>
                <button
                  type="submit"
                  :disabled="saving"
                  class="flex-[2] px-6 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-blue-700 transition-all shadow-xl shadow-blue-200 dark:shadow-none hover:translate-y-[-2px] active:scale-95 flex items-center justify-center gap-2"
                >
                  <Icon v-if="saving" icon="mdi:loading" class="animate-spin text-lg" />
                  {{ saving ? 'Sincronizando...' : (editingEstado ? 'Actualizar Regla' : 'Confirmar Estado') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal de Detalles Premium -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showDetailModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden transform transition-all relative">
            <div :class="`h-32 relative overflow-hidden bg-gradient-to-br from-${getColorClass(selectedEstado?.color)}-600 to-${getColorClass(selectedEstado?.color)}-800 text-white`">
              <div class="absolute inset-0 opacity-10">
                <Icon icon="mdi:credit-card-settings" class="text-[12rem] -translate-x-10 -translate-y-10 rotate-12" />
              </div>
              <button @click="closeDetailModal" class="absolute top-4 right-4 p-2.5 bg-white/20 hover:bg-white/30 rounded-2xl transition-all text-white active:scale-90 z-10">
                <Icon icon="mdi:close" class="text-xl" />
              </button>
              
              <div class="absolute -bottom-10 left-8">
                <div class="w-20 h-20 rounded-3xl bg-white dark:bg-gray-800 flex items-center justify-center shadow-2xl border-4 border-white dark:border-gray-800 transition-transform hover:scale-105">
                  <Icon icon="mdi:tag-multiple" :class="['text-4xl', `text-${getColorClass(selectedEstado?.color)}-600`]" />
                </div>
              </div>
            </div>
            
            <div v-if="selectedEstado" class="p-8 pt-14">
              <div class="mb-8">
                <h3 class="text-3xl font-black text-gray-900 dark:text-white leading-tight mb-1">{{ selectedEstado.nombre }}</h3>
                <div :class="[
                  'inline-flex items-center px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.2em] font-sans',
                  `bg-${getColorClass(selectedEstado.color)}-50 text-${getColorClass(selectedEstado.color)}-600 dark:bg-${getColorClass(selectedEstado.color)}-900/30 dark:text-${getColorClass(selectedEstado.color)}-400`
                ]">
                  <span :class="['w-1.5 h-1.5 rounded-full mr-2 shadow-sm animate-pulse font-sans', `bg-${getColorClass(selectedEstado.color)}-500`]"></span>
                  Semántica: {{ getColorName(selectedEstado.color) }}
                </div>
              </div>
              
              <div class="space-y-6">
                <div class="p-5 bg-gray-50 dark:bg-gray-900/50 rounded-3xl border border-gray-100 dark:border-gray-700 relative overflow-hidden group">
                  <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Contexto Aplicacional</p>
                  <p class="text-sm font-bold text-gray-600 dark:text-gray-300 italic italic">
                    {{ selectedEstado.descripcion || 'Este estado financiero no posee una definición detallada en la base de conocimientos.' }}
                  </p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <div class="p-5 bg-gray-50 dark:bg-gray-900/50 rounded-3xl border border-gray-100 dark:border-gray-700">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Identificador</p>
                    <p class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tighter">REF-{{ selectedEstado.idEstadoPago }}</p>
                  </div>
                  <div class="p-5 bg-gray-50 dark:bg-gray-900/50 rounded-3xl border border-gray-100 dark:border-gray-700">
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Registro</p>
                    <p class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tighter">{{ formatDate(selectedEstado.fecha_creacion || selectedEstado.fechaCreacion) }}</p>
                  </div>
                </div>
                
                <div class="pt-6 flex gap-3">
                  <button
                    @click="openEditModal(selectedEstado); closeDetailModal()"
                    class="flex-1 px-6 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:scale-[1.02] active:scale-95 transition-all"
                  >
                    Actualizar Regla
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal de Confirmación Premium -->
    <Teleport to="body">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
        <div v-if="showConfirmModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[100] backdrop-blur-[2px]">
          <div class="bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden transform transition-all">
            <div :class="[
              'px-8 py-8 text-center border-b border-gray-50 dark:border-gray-700',
              confirmConfig.type === 'danger' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-50 dark:bg-blue-900/20'
            ]">
              <div :class="[
                'w-16 h-16 mx-auto rounded-[1.5rem] flex items-center justify-center mb-6 shadow-xl transition-transform hover:rotate-12 text-white',
                confirmConfig.type === 'danger' ? 'bg-red-600 shadow-red-200' : 'bg-blue-600 shadow-blue-200'
              ]">
                <Icon :icon="confirmConfig.type === 'danger' ? 'mdi:alert-octagon-outline' : 'mdi:help-circle-outline'" class="text-3xl" />
              </div>
              <h3 class="text-xl font-black text-gray-900 dark:text-white leading-tight mb-2 capitalize">{{ confirmConfig.title }}</h3>
              <div class="text-sm font-bold text-gray-500 dark:text-gray-400" v-html="confirmConfig.message"></div>
            </div>

            <div class="p-8 flex gap-4">
              <button
                @click="cancelConfirm"
                class="flex-1 px-6 py-4 border border-gray-100 dark:border-gray-700 rounded-2xl text-gray-400 font-bold text-[10px] uppercase tracking-widest hover:bg-gray-50 transition-all active:scale-95"
              >
                {{ confirmConfig.cancelText }}
              </button>
              <button
                @click="executeConfirm"
                :class="[
                  'flex-1 px-6 py-4 rounded-2xl text-white font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-xl hover:translate-y-[-2px] active:scale-95',
                  confirmConfig.type === 'danger' ? 'bg-red-600 shadow-red-200' : 'bg-blue-600 shadow-blue-200'
                ]"
              >
                {{ confirmConfig.confirmText }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";

// Importar funciones de la API de estados de pago
import {
  obtenerEstadosPago,
  crearEstadoPago,
  actualizarEstadoPago,
  eliminarEstadoPago,
  verificarEstadoPagoPorNombre,
  buscarEstadosPagoLocal,
  obtenerEstadisticasEstadosPago,
  ordenarEstadosPago,
  obtenerClaseColor
} from '@/api/estadoPago.js';

// Importar generador de reportes (similar al de categorías)
import {
  imprimirReporteEstadosPago,
  imprimirReporteSimplificadoEstadosPago
} from '@/utils/reporteEstadosPagoGenerator.js';

export default {
  name: 'EstadosPagoList',
  components: {
    Icon,
  },
  data() {
    return {
      estados: [],
      loading: false,
      saving: false,
      showModal: false,
      showDetailModal: false,
      showExportMenu: false,
      showConfirmModal: false,
      confirmConfig: {
        title: 'Confirmar',
        message: '',
        detail: '',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        type: 'info',
        onConfirm: null,
        onCancel: null
      },
      searchQuery: '',
      filterColor: 'all',
      sortBy: 'nombre',
      currentPage: 1,
      itemsPerPage: 10,
      form: {
        idEstadoPago: null,
        nombre: '',
        descripcion: '',
        color: 'secondary',
      },
      errors: {},
      editingEstado: null,
      selectedEstado: null,
      lastUpdate: new Date(),
      colorOptions: [
        { value: 'success', label: 'Éxito', colorClass: 'green' },
        { value: 'warning', label: 'Advertencia', colorClass: 'amber' },
        { value: 'danger', label: 'Peligro', colorClass: 'red' },
        { value: 'info', label: 'Información', colorClass: 'cyan' },
        { value: 'primary', label: 'Primario', colorClass: 'blue' },
        { value: 'secondary', label: 'Secundario', colorClass: 'gray' },
        { value: 'dark', label: 'Oscuro', colorClass: 'gray' },
        { value: 'light', label: 'Claro', colorClass: 'gray' }
      ]
    };
  },
  computed: {
    filteredEstados() {
      let filtered = [...this.estados];
      
      if (this.searchQuery.trim()) {
        filtered = buscarEstadosPagoLocal(filtered, this.searchQuery);
      }
      
      if (this.filterColor !== 'all') {
        filtered = filtered.filter(e => e.color === this.filterColor);
      }
      
      filtered = ordenarEstadosPago(filtered, this.sortBy);
      
      return filtered;
    },
    
    estadisticas() {
      return obtenerEstadisticasEstadosPago(this.estados);
    },
    
    paginatedEstados() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredEstados.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredEstados.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredEstados.length);
    },
    
    isFormValid() {
      return this.form.nombre.trim().length > 0 && this.form.color;
    },
    
    isFiltered() {
      return this.searchQuery.trim() || this.filterColor !== 'all';
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.loadEstadosPago();
  },
  watch: {
    filterColor() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    showConfirm(options) {
      return new Promise((resolve) => {
        this.confirmConfig = {
          title: options.title || 'Confirmar',
          message: options.message || '¿Estás seguro?',
          detail: options.detail || '',
          confirmText: options.confirmText || 'Aceptar',
          cancelText: options.cancelText || 'Cancelar',
          type: options.type || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        };
        this.showConfirmModal = true;
      });
    },
    
    cancelConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onCancel) {
        this.confirmConfig.onCancel();
      }
    },
    
    executeConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onConfirm) {
        this.confirmConfig.onConfirm();
      }
    },
    
    async loadEstadosPago() {
      this.loading = true;
      try {
        const response = await obtenerEstadosPago();
        
        if (response.success) {
          this.estados = response.data || [];
          this.lastUpdate = new Date();
          console.log('✅ Estados de pago cargados:', this.estados.length);
        } else {
          throw new Error(response.error || 'Error al cargar estados de pago');
        }
      } catch (error) {
        console.error('❌ Error al cargar estados de pago:', error);
        this.toast.error('Error al cargar los estados de pago: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    handleFilterChange() {
      this.currentPage = 1;
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
    
    openCreateModal() {
      this.editingEstado = null;
      this.resetForm();
      this.errors = {};
      this.showModal = true;
    },
    
    openEditModal(estado) {
      this.editingEstado = estado;
      this.form = {
        idEstadoPago: estado.idEstadoPago,
        nombre: estado.nombre,
        descripcion: estado.descripcion || '',
        color: estado.color || 'secondary',
      };
      this.errors = {};
      this.showModal = true;
    },
    
    openDetailModal(estado) {
      this.selectedEstado = estado;
      this.showDetailModal = true;
    },
    
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errors = {};
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedEstado = null;
    },
    
    resetForm() {
      this.form = {
        idEstadoPago: null,
        nombre: '',
        descripcion: '',
        color: 'secondary',
      };
      this.editingEstado = null;
    },
    
    validateForm() {
      this.errors = {};
      
      if (!this.form.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      } else if (this.form.nombre.length > 50) {
        this.errors.nombre = 'El nombre no puede exceder 50 caracteres';
      } else if (!/^[a-záéíóúñ\s]+$/i.test(this.form.nombre)) {
        this.errors.nombre = 'El nombre solo puede contener letras y espacios';
      }
      
      if (this.form.descripcion && this.form.descripcion.length > 200) {
        this.errors.descripcion = 'La descripción no puede exceder 200 caracteres';
      }
      
      if (!this.form.color) {
        this.errors.color = 'Debes seleccionar un color';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async saveEstado() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        if (!this.editingEstado) {
          const verifyResult = await verificarEstadoPagoPorNombre(this.form.nombre);
          
          if (verifyResult.success && verifyResult.existe) {
            this.errors.nombre = 'Este estado ya está registrado';
            this.saving = false;
            return;
          }
        }

        const estadoData = {
          nombre: this.form.nombre.trim().toLowerCase(),
          descripcion: this.form.descripcion.trim() || null,
          color: this.form.color
        };

        let result;
        if (this.editingEstado) {
          result = await actualizarEstadoPago(this.editingEstado.idEstadoPago, estadoData);
        } else {
          result = await crearEstadoPago(estadoData);
        }

        if (result.success) {
          await this.loadEstadosPago();
          this.closeModal();
          this.toast.success(
            this.editingEstado ? 'Estado actualizado correctamente' : 'Estado creado correctamente'
          );
        } else {
          throw new Error(result.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('❌ Error al guardar estado:', error);
        this.toast.error(error.message || 'Error al guardar el estado');
      } finally {
        this.saving = false;
      }
    },
    
    async confirmDelete(estado) {
      const confirmed = await this.showConfirm({
        title: '¿Eliminar estado?',
        message: `¿Estás seguro de eliminar <strong>"${this.capitalize(estado.nombre)}"</strong>?`,
        detail: 'Esta acción no se puede deshacer. Si hay pagos asociados a este estado, no podrás eliminarlo.',
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      });
      
      if (confirmed) {
        await this.deleteEstado(estado.idEstadoPago, estado.nombre);
      }
    },
    
    async deleteEstado(id, nombre) {
      try {
        const result = await eliminarEstadoPago(id);
        if (result.success) {
          await this.loadEstadosPago();
          this.toast.success(`El estado "${this.capitalize(nombre)}" ha sido eliminado`);
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error al eliminar estado:', error);
        this.toast.error(error.message || 'Error al eliminar el estado');
      }
    },
    
    printFullReport() {
      if (this.filteredEstados.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        const filtros = {
          color: this.filterColor,
          busqueda: this.searchQuery,
          orden: this.sortBy
        };
        
        imprimirReporteEstadosPago(
          this.filteredEstados,
          'REPORTE DE ESTADOS DE PAGO - JEAN INFORMATICO',
          filtros
        );
        
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },
    
    printSimplifiedReport() {
      if (this.filteredEstados.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }

      try {
        imprimirReporteSimplificadoEstadosPago(
          this.filteredEstados,
          'LISTADO DE ESTADOS DE PAGO - JEAN INFORMATICO'
        );
        
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },
    
    printCurrentView() {
      this.printFullReport();
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.filterColor = 'all';
      this.sortBy = 'nombre';
      this.currentPage = 1;
    },
    
    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    
    getColorName(color) {
      const colors = {
        success: 'Éxito (Verde)',
        warning: 'Advertencia (Amarillo)',
        danger: 'Peligro (Rojo)',
        info: 'Información (Celeste)',
        primary: 'Primario (Azul)',
        secondary: 'Secundario (Gris)',
        dark: 'Oscuro',
        light: 'Claro'
      };
      return colors[color] || color || 'Secundario';
    },
    
    getColorClass(color) {
      const classes = {
        success: 'green',
        warning: 'amber',
        danger: 'red',
        info: 'cyan',
        primary: 'blue',
        secondary: 'gray',
        dark: 'gray',
        light: 'gray'
      };
      return classes[color] || 'gray';
    },
    
    getColorIcon(color) {
      const icons = {
        success: 'mdi:check-circle',
        warning: 'mdi:alert',
        danger: 'mdi:close-circle',
        info: 'mdi:information',
        primary: 'mdi:star',
        secondary: 'mdi:circle',
        dark: 'mdi:circle',
        light: 'mdi:circle'
      };
      return icons[color] || 'mdi:circle';
    },
    
    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return fechaISO;
      }
    },
    
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '';
      }
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>